'use client';

import { useCreateCheckoutMutation } from '../api/billingApi';
import type { Interval, Provider, Tier } from '../types/billing.types';

interface StartCheckoutArgs {
  tier: Exclude<Tier, 'free' | 'starter'>;
  interval: Interval;
  provider: Provider;
}

/**
 * Initiates checkout. Stripe response carries a `checkout_url` to redirect to;
 * crypto response carries an `invoice_url` plus on-screen pay details (caller
 * decides how to render those).
 */
export function useCheckout() {
  const [createCheckout, state] = useCreateCheckoutMutation();

  async function startCheckout({ tier, interval, provider }: StartCheckoutArgs) {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : '';
    const result = await createCheckout({
      tier,
      interval,
      provider,
      // `{CHECKOUT_SESSION_ID}` is a Stripe template — substituted with the
      // real session id at redirect time so the success page can show a
      // reference if the webhook is briefly delayed.
      success_url: `${origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/billing/cancel`,
    }).unwrap();
    if (provider === 'stripe' && result.checkout_url) {
      window.location.assign(result.checkout_url);
    }
    return result;
  }

  return { startCheckout, ...state };
}
